import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Layers, BarChart3, Users, AlertTriangle, Globe } from 'lucide-react';

// Custom platform images
const PLATFORM_IMAGES = {
  dashboard: "https://customer-assets.emergentagent.com/job_regulated-payments/artifacts/8ijfi4c9_dash_board_small.png",
  worldwide: "https://customer-assets.emergentagent.com/job_regulated-payments/artifacts/vi3j4kkl_new_worldwide_connectivity2.png",
};

const platformFeatures = [
  {
    icon: Layers,
    title: 'Aggregation Gateway',
    description: 'Multi-rail payment routing across banks, PSPs and card networks with intelligent failover.',
    span: 'col-span-1 lg:col-span-2',
    hasImage: false
  },
  {
    icon: BarChart3,
    title: 'Real-Time Dashboard',
    description: 'Full transaction visibility, settlement tracking, KYC status and reporting in one interface.',
    span: 'col-span-1 lg:col-span-2',
    hasImage: true,
    image: PLATFORM_IMAGES.dashboard,
    imagePosition: 'large'
  },
  // Second row: Worldwide Connectivity first, then Multi-Tenant, then Compliance
  {
    icon: Globe,
    title: 'Worldwide Connectivity',
    description: '60+ pre-built integrations with banks, FX providers, card networks and identity services.',
    span: 'col-span-1 lg:col-span-2',
    hasImage: true,
    image: PLATFORM_IMAGES.worldwide,
    imagePosition: 'large'
  },
  {
    icon: Users,
    title: 'Multi-Tenant Architecture',
    description: 'White-label ready platform supporting multiple clients and configurations.',
    span: 'col-span-1',
    hasImage: false
  },
  {
    icon: AlertTriangle,
    title: 'Compliance Engine',
    description: 'Integrated AML monitoring, sanctions screening and regulatory reporting.',
    span: 'col-span-1',
    hasImage: false
  }
];

export const PlatformSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section 
      id="platform" 
      className="py-16 lg:py-20 bg-[#E8E8E8] border-t border-[#D1D5DB]"
      data-testid="platform-section"
    >
      <div className="section-container">
        {/* Section Header */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="mb-10"
        >
          <span className="label-text text-[#6B7280] block mb-4">
            Our Platform
          </span>
          <h2 
            className="heading-lg text-3xl md:text-4xl lg:text-5xl text-[#1E3A5F] mb-4"
            data-testid="platform-title"
          >
            Proprietary Technology
          </h2>
          <p className="text-[#6B7280] text-lg">
            Real-Time · Fully Managed
          </p>
        </motion.div>

        {/* Platform Features Grid - Bento Style */}
        <div 
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#D1D5DB]"
          data-testid="platform-features-grid"
        >
          {platformFeatures.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ 
                duration: 0.6, 
                delay: index * 0.1, 
                ease: [0.22, 1, 0.36, 1] 
              }}
              className={`${feature.span} bg-white p-8 lg:p-10 card-hover relative overflow-hidden`}
              data-testid={`platform-feature-${index}`}
            >
              {/* Feature Content */}
              <div className="relative z-10">
                <feature.icon 
                  className="w-7 h-7 text-[#1E3A5F] mb-6" 
                  strokeWidth={1.25} 
                />
                <h3 className="text-xl font-semibold text-[#1E3A5F] mb-3">
                  {feature.title}
                </h3>
                <p className="text-base text-[#6B7280] leading-relaxed">
                  {feature.description}
                </p>
              </div>
              
              {/* Feature Image (for certain features) */}
              {feature.hasImage && feature.image && feature.imagePosition === 'large' && (
                <div className="mt-8 -mx-8 lg:-mx-10 -mb-8 lg:-mb-10">
                  <img 
                    src={feature.image}
                    alt={`${feature.title} Interface`}
                    className="w-full h-auto"
                  />
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
